/**
 * Loyalty portal client — points balance, ledger, tier redemption.
 *
 * Auth: shares the App Proxy session route with the subscriptions backend.
 * One JWT covers /v1/me/loyalty/* and /v1/me/reviews because it's the same
 * service. Token is cached on this instance; refresh on 401.
 */

const API_BASE = 'https://subs-api.highereducation.shop/v1/me';
const APP_PROXY_SESSION = '/apps/subscriptions/session';

export class LoyaltyClient {
  /** @type {string | null} */
  #token = null;
  /** @type {number | null} */
  #tokenExpiresAt = null;

  async #ensureToken() {
    const now = Math.floor(Date.now() / 1000);
    if (this.#token && this.#tokenExpiresAt && this.#tokenExpiresAt - now > 60) {
      return this.#token;
    }
    let resp;
    try {
      resp = await fetch(APP_PROXY_SESSION, {
        method: 'GET',
        credentials: 'include',
        headers: { Accept: 'application/json' },
      });
    } catch (err) {
      throw { code: 'network', cause: err };
    }
    if (resp.status === 401) {
      this.#token = null;
      this.#tokenExpiresAt = null;
      throw { code: 'not_logged_in' };
    }
    if (!resp.ok) throw { code: 'proxy_error', status: resp.status };
    const body = await resp.json();
    if (!body.token) throw { code: 'proxy_error', reason: 'no_token' };
    this.#token = body.token;
    this.#tokenExpiresAt = now + (body.expiresIn ?? 3600);
    return this.#token;
  }

  async #call(path, init = {}) {
    const token = await this.#ensureToken();
    const resp = await fetch(`${API_BASE}${path}`, {
      ...init,
      headers: {
        ...(init.headers ?? {}),
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    if (resp.status === 401) {
      this.#token = null;
      this.#tokenExpiresAt = null;
      const retryToken = await this.#ensureToken();
      const retry = await fetch(`${API_BASE}${path}`, {
        ...init,
        headers: {
          ...(init.headers ?? {}),
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${retryToken}`,
        },
      });
      return this.#handleResponse(retry);
    }
    return this.#handleResponse(resp);
  }

  async #handleResponse(resp) {
    const ct = resp.headers.get('content-type') ?? '';
    const body = ct.includes('application/json') ? await resp.json() : null;
    if (!resp.ok) {
      throw { code: body?.error ?? 'http_error', status: resp.status, body };
    }
    return body;
  }

  /** @returns {Promise<{ currentBalance: number, lifetimeEarned: number, lifetimeRedeemed: number }>} */
  getBalance() {
    return this.#call('/loyalty/balance', { method: 'GET' });
  }

  /** @returns {Promise<{ entries: LedgerEntry[] }>} */
  getLedger({ limit = 50, offset = 0 } = {}) {
    const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
    return this.#call(`/loyalty/ledger?${params.toString()}`, { method: 'GET' });
  }

  /** @returns {Promise<{ tiers: Tier[] }>} */
  getTiers() {
    return this.#call('/loyalty/tiers', { method: 'GET' });
  }

  /** @returns {Promise<RedeemResult>} */
  redeem(points) {
    return this.#call('/loyalty/redeem', {
      method: 'POST',
      body: JSON.stringify({ points }),
    });
  }
}

export function describeLoyaltyError(err) {
  const e = /** @type {any} */ (err);
  switch (e?.code) {
    case 'not_logged_in':
      return 'Please sign in to view your points.';
    case 'network':
      return 'Network problem. Please try again.';
    case 'invalid_tier':
      return 'That redemption isn\'t available right now.';
    case 'insufficient_balance':
      return 'You don\'t have enough points for that yet.';
    case 'redeem_failed':
      return 'Redemption failed. Please try again or contact support.';
    case 'no_email_in_session':
      return 'We couldn\'t read your account email. Try signing out and back in.';
    default:
      return 'Something went wrong. Please try again.';
  }
}

/**
 * @typedef {Object} Tier
 * @property {number} id
 * @property {number} points_cost
 * @property {number} discount_value_cents
 *
 * @typedef {Object} LedgerEntry
 * @property {string} id
 * @property {number} amount
 * @property {string} kind
 * @property {string|null} order_id
 * @property {string|null} review_id
 * @property {string|null} discount_code
 * @property {number|null} discount_value_cents
 * @property {string|null} code_expires_at
 * @property {string} created_at
 *
 * @typedef {Object} RedeemResult
 * @property {string} code
 * @property {number} discount_value_cents
 * @property {string} expires_at
 * @property {number} points_spent
 * @property {number} new_balance
 */
